import React, { useMemo, useState, useEffect, useCallback, useRef } from 'react'
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable
} from '@tanstack/react-table'
import { TextInput } from '@siftscience/focus-components/input'
import { Button, IconButton } from '@siftscience/focus-components/button'
import { Delete } from '@siftscience/focus-components/icons/Delete'
import { Title } from '@siftscience/focus-components/text'
import { makeStyles } from '@material-ui/core/styles'
import { capitalize, snakeCase, cloneDeep, isEqual } from 'lodash'
import { RowData } from './nested-array-field'

const useStyles = makeStyles(theme => ({
  subTableWrapper: { marginBottom: '16px' },
  requiredAsterisk: { color: '#eb3b2e' },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    tableLayout: 'auto',
    '& th, & td': {
      border: `1px solid ${theme.palette.divider}`,
      padding: '8px',
      textAlign: 'left',
      whiteSpace: 'nowrap'
    },
    '& th': { backgroundColor: '#f2f2f2' }
  },
  tableContainer: {
    display: 'block',
    width: 'inherit',
    overflowX: 'auto',
    paddingRight: '30px',
    '-webkit-overflow-scrolling': 'touch'
  }
}))

const getPropertiesFromSchema = schema => {
  if (!schema) return {}
  const items = schema.items
  if (!items) return {}
  const itemSchema = Array.isArray(items) ? items[0] : items
  return itemSchema?.properties?.properties || itemSchema?.properties || {}
}

const getRequiredFieldsFromSchema = schema => {
  if (!schema) return []
  const items = schema.items
  if (!items) return []
  const itemSchema = Array.isArray(items) ? items[0] : items
  return itemSchema?.properties?.required || itemSchema?.required || []
}

const getIsPrimitiveArray = schema => {
  if (!schema) return false
  const items = schema.items
  if (!items) return false
  const itemSchema = Array.isArray(items) ? items[0] : items
  const type = Array.isArray(itemSchema.type)
    ? itemSchema.type[0]
    : itemSchema.type
  // It's a primitive if it's a string and doesn't have properties.
  return type === 'string' && !itemSchema.properties
}

const SubTable = React.memo(
  ({
    data,
    schema,
    title,
    onSubChange,
    onBlur
  }: {
    data: RowData[]
    schema: any
    title: string
    onSubChange: (newSubArray: RowData[]) => void
    onBlur: () => void
  }) => {
    const classes = useStyles()
    const isPrimitive = useMemo(() => getIsPrimitiveArray(schema), [schema])

    const transformData = useCallback(
      d => {
        if (isPrimitive) {
          return Array.isArray(d) ? d.map(value => ({ value })) : []
        }
        return cloneDeep(d || [])
      },
      [isPrimitive]
    )

    const [internalData, setInternalData] = useState(() => transformData(data))
    const internalDataRef = useRef(internalData)
    useEffect(() => {
      internalDataRef.current = internalData
    }, [internalData])

    useEffect(() => {
      const transformedNewData = transformData(data)
      if (!isEqual(internalDataRef.current, transformedNewData)) {
        setInternalData(transformedNewData)
      }
    }, [data, transformData])

    const properties = useMemo(() => {
      if (isPrimitive) {
        return { value: { title: 'Value', type: 'string' } }
      }
      return getPropertiesFromSchema(schema)
    }, [schema, isPrimitive])

    const requiredFields = useMemo(
      () => getRequiredFieldsFromSchema(schema),
      [schema]
    )

    const propagateChanges = useCallback(
      newData => {
        const dataToPropagate = isPrimitive
          ? newData.map(d => d.value)
          : newData
        onSubChange(dataToPropagate)
        onBlur()
      },
      [isPrimitive, onSubChange, onBlur]
    )

    const handleLocalChange = useCallback(
      (value: string, index: number, key: string) => {
        const newArray = [...internalDataRef.current]
        if (isPrimitive) {
          newArray[index] = { value }
        } else {
          newArray[index] = { ...newArray[index], [key]: value }
        }
        setInternalData(newArray)
      },
      [isPrimitive]
    )

    const handleBlurAndPropagate = useCallback(() => {
      propagateChanges(internalDataRef.current)
    }, [propagateChanges])

    const handleAddRow = useCallback(() => {
      const newRow = isPrimitive
        ? { value: '' }
        : Object.keys(properties).reduce((acc, key) => {
            acc[snakeCase(key)] = ''
            return acc
          }, {})
      const newData = [...internalDataRef.current, newRow]
      setInternalData(newData)
      propagateChanges(newData)
    }, [isPrimitive, properties, propagateChanges])

    const handleDeleteRow = useCallback(
      (index: number) => {
        const newArray = [...internalDataRef.current]
        newArray.splice(index, 1)
        setInternalData(newArray)
        propagateChanges(newArray)
      },
      [propagateChanges]
    )

    const columns: ColumnDef<RowData>[] = useMemo(() => {
      const requiredFieldsHash = requiredFields.reduce((hash, field) => {
        hash[field] = true
        return hash
      }, {})
      return [
        ...Object.keys(properties).map(key => {
          const snakeCaseKey = isPrimitive ? 'value' : snakeCase(key)
          const isRequired =
            requiredFieldsHash[properties[key]?.key || key] || false
          return {
            accessorKey: snakeCaseKey,
            header: () => (
              <>
                {capitalize(properties[key]?.title || key)}
                {isRequired && (
                  <span className={classes.requiredAsterisk}>*</span>
                )}
              </>
            ),
            cell: ({ row, getValue }) => {
              const value = getValue<string>() || ''
              return (
                <TextInput
                  value={value}
                  onChange={e =>
                    handleLocalChange(e.target.value, row.index, snakeCaseKey)
                  }
                  onBlur={handleBlurAndPropagate}
                  containerStyle={{ width: '100%' }}
                  required={isRequired}
                  containerClassName={`${isRequired && !value ? 'error' : ''}`}
                />
              )
            }
          }
        }),
        {
          id: 'delete',
          cell: ({ row }) => (
            <IconButton
              variant="secondary-ghost"
              onClick={() => handleDeleteRow(row.index)}
            >
              <Delete />
            </IconButton>
          )
        }
      ]
    }, [
      properties,
      requiredFields,
      isPrimitive,
      handleLocalChange,
      handleBlurAndPropagate,
      handleDeleteRow
    ])

    const table = useReactTable({
      data: internalData,
      columns,
      getCoreRowModel: getCoreRowModel()
    })

    return (
      <div>
        <Title as="h5">{capitalize(title)}</Title>
        <div className={classes.tableContainer}>
          <table className={classes.table}>
            <thead>
              {table.getHeaderGroups().map(headerGroup => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map(header => (
                    <th key={header.id}>
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map(row => (
                <tr key={row.id}>
                  {row.getVisibleCells().map(cell => (
                    <td key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Button
          variant="secondary"
          lined
          onClick={handleAddRow}
          style={{ marginTop: '8px' }}
        >
          + Add {capitalize(title.slice(0, -1))}
        </Button>
      </div>
    )
  }
)

export default SubTable
